import { Box, Button, Card, CircularProgress, Tooltip } from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DataGrid, GridColDef, GridToolbar, GridCellParams, enUS, frFR } from '@mui/x-data-grid/';
import i18next from 'i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AuthHeader } from '../../../../utils/helpers/auth-headers';
import { GridRowParticipant } from '../../../../models/pexip-model';
import RealTimeDetails from '../RealTimeDetails/RealTimeDetails';
import { PageRowsOptions } from '../../../../models/DataGridConf';
import { RootState } from '../../../../stores/globalStore';
import { setConnectedUser } from '../../../../stores/authenticationStore';

function RealTimeParticipants() {
  const { t } = useTranslation();

  const connectedUser = useSelector((state: RootState) => state.authentication.connectedUser);

  const dispatch = useDispatch();

  const [participantsData, setParticipantsData] = useState<any>([]);
  const [participantsCount, setParticipantsCount] = useState<number>(0);
  const [meetingName, setMeetingName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [gridLang, setGridLang] = useState(frFR);
  const [selectionModel, setSelectionModel] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<GridRowParticipant>({} as GridRowParticipant);
  const [pageSize, setPageSize] = useState<number>(connectedUser.prefs?.nbRow ? connectedUser.prefs.nbRow : 25);
  const pageRowsOptions = PageRowsOptions;
  const paramsUrl = useParams();
  const navigate = useNavigate();
  const i18nLang = i18next.language;

  useEffect(() => {
    if (i18nLang === 'en') {
      setGridLang(enUS);
    } else {
      setGridLang(frFR);
    }
  }, [i18nLang]);

  function getMeetingDetails(instanceId: string, meetingId: string) {
    return axios.get(`${window._env_.REACT_APP_API_URI}/pexipInstance/${instanceId}/liveMeeting/${meetingId}/participant`, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => response.data);
  }

  useEffect(() => {
    if (paramsUrl.instanceId && paramsUrl.meetingId) {
      setIsLoading(true);
      getMeetingDetails(paramsUrl.instanceId, paramsUrl.meetingId).then((r) => {
        setParticipantsCount(r.data.participantCount);
        setMeetingName(r.data.meetingName);
        setParticipantsData(r.data.participants);
      }).then(() => {
        setIsLoading(false);
      });
    }
  }, [paramsUrl]);

  const cancelDetails = () => {
    const url = `/instance/${paramsUrl.instanceId}/real-time`;
    navigate(url);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      hide: true
    },
    {
      field: 'displayName',
      renderHeader: () => (
        <Tooltip title={t('RealTimeList.table.name') as string} arrow>
          <span className="strong-header">{t('RealTimeList.table.name')}</span>
        </Tooltip>
      ),
      type: 'string',
      flex: 1
    },
    {
      field: 'role',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.role') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.role')}</span>
        </Tooltip>
      ),
      renderCell: ((params: GridCellParams) => {
        if (params.row.role === 'chair') {
          return <p>{t('RealTimeDetails.details.chair')}</p>;
        }
        return <p>{t('RealTimeDetails.details.guest')}</p>;
      }),
      type: 'string',
      flex: 1
    },
    {
      field: 'connectTime',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.connectTime') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.connectTime')}</span>
        </Tooltip>
      ),
      renderCell: ((params: GridCellParams) => {
        const time = new Date(params.row.connectTime).toLocaleTimeString(i18nLang);
        return (<span>{time}</span>);
      }),
      type: 'string',
      flex: 1
    },
    {
      field: 'callQualityNow',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.callQuality') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.callQuality')}</span>
        </Tooltip>
      ),
      renderCell: ((params: GridCellParams) => {
        let formattedValue;
        switch (params.row.callQualityNow) {
        case '0_unknown':
          formattedValue = <p style={{ color: '#94A2B0' }}>{t('RealTimeDetails.details.quality.unknown')}</p>;
          break;
        case '1_good':
          formattedValue = <p style={{ color: '#93B37A' }}>{t('RealTimeDetails.details.quality.good')}</p>;
          break;
        case '2_ok':
          formattedValue = <p style={{ color: '#E1BC66' }}>{t('RealTimeDetails.details.quality.ok')}</p>;
          break;
        case '3_bad':
          formattedValue = <p style={{ color: '#d77947' }}>{t('RealTimeDetails.details.quality.bad')}</p>;
          break;
        case '4_terrible':
          formattedValue = <p style={{ color: '#E05D5F' }}>{t('RealTimeDetails.details.quality.terrible')}</p>;
          break;
        default:
          break;
        }
        return formattedValue;
      }),
      type: 'string',
      flex: 1
    },
    {
      field: 'isMuted',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.isMuted') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.isMuted')}</span>
        </Tooltip>
      ),
      renderCell: ((params: GridCellParams) => {
        if (params.row.isMuted) {
          return <p>{t('RealTimeDetails.details.yes')}</p>;
        }
        return <p>{t('RealTimeDetails.details.no')}</p>;
      }),
      type: 'string',
      flex: 1
    },
    {
      field: 'hasMedia',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.hasMedia') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.hasMedia')}</span>
        </Tooltip>
      ),
      renderCell: ((params: GridCellParams) => {
        if (params.row.hasMedia) {
          return <p>{t('RealTimeDetails.details.yes')}</p>;
        }
        return <p>{t('RealTimeDetails.details.no')}</p>;
      }),
      type: 'string',
      flex: 1
    },
    {
      field: 'protocol',
      renderHeader: () => (
        <Tooltip title={t('RealTimeDetails.details.protocol') as string} arrow>
          <span className="strong-header">{t('RealTimeDetails.details.protocol')}</span>
        </Tooltip>
      ),
      type: 'string',
      flex: 1
    },
  ];

  function buildRows(data: any) {
    const rows: GridRowParticipant[] = [];
    for (let i = 0; i < data.length; i += 1) {
      const row = {
        id: data[i].lastData.uuid,
        displayName: data[i].lastData.displayName,
        protocol: data[i].lastData.protocol,
        isPresenting: data[i].lastData.isPresenting,
        connectTime: data[i].lastData.connectTime,
        serviceTag: data[i].lastData.serviceTag,
        callTag: data[i].lastData.callTag,
        mediaNode: data[i].lastData.mediaNode,
        uuid: data[i].lastData.uuid,
        callId: data[i].lastData.callId,
        vendor: data[i].lastData.vendor,
        role: data[i].lastData.role,
        conversationId: data[i].lastData.conversationId,
        destinationAlias: data[i].lastData.destinationAlias,
        isStreaming: data[i].lastData.isStreaming,
        isMuted: data[i].lastData.isMuted,
        remoteAddress: data[i].lastData.remoteAddress,
        deviceOsName: data[i].device?.os?.name,
        deviceOsVersion: data[i].device?.os?.version,
        deviceOsPlatform: data[i].device?.os?.platform,
        clientType: data[i].device?.client?.type,
        clientName: data[i].device?.client?.name,
        clientVersion: data[i].device?.client?.version,
        deviceType: data[i].device?.device?.type,
        deviceBrand: data[i].device?.device?.brand,
        deviceModel: data[i].device?.device?.model,
        callQualityNow: data[i].lastMediaStream?.callQualityNow,
        hasMedia: data[i].lastData.hasMedia
      };
      rows.push(row);
    }
    return rows;
  }

  const closeModal = () => {
    setOpen(false);
    setSelectionModel([]);
  };

  const updateNbRowUser = (updateNbRow: number) => {  
    const updatePrefs = { ...connectedUser.prefs };
    updatePrefs.nbRow = updateNbRow;
    const modifyPrefs = {
      prefs: updatePrefs
    };
    axios.post(`${window._env_.REACT_APP_API_URI}/user/prefs`, modifyPrefs, { headers: AuthHeader() ? { Authorization: `Bearer ${AuthHeader()}` } : {} })
      .then((response) => {
        dispatch(setConnectedUser({ connectedUser: response.data.data }));
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const changePageSize = (newPage: number) => {
    setPageSize(newPage);
    updateNbRowUser(newPage);
  };

  return (
    <Card sx={{ height: 651, overflow: 'auto' }}>
      <div className="card-title">
        {meetingName}, {t('RealTimeDetails.participants')} ({participantsCount})
      </div>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          <Box sx={{ height: 550, overflow: 'auto' }}>
            <DataGrid
              rows={buildRows(participantsData)}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={pageRowsOptions}
              onPageSizeChange={(newPage) => changePageSize(newPage)}
              components={{ Toolbar: GridToolbar }}
              pagination
              onCellClick={(params: GridCellParams) => {
                setSelectedParticipant(params.row);
                setOpen(true);
              }}
              getRowClassName={() => 'pointer'}
              hideFooterSelectedRowCount
              localeText={gridLang.components.MuiDataGrid.defaultProps.localeText}
              density="compact"
              selectionModel={selectionModel}
              onSelectionModelChange={setSelectionModel}
            />
          </Box>
          <RealTimeDetails selectedParticipant={selectedParticipant} isOpen={open} closeModal={() => closeModal()} />
          <div style={{ padding: 10 }}>
            <Button
              fullWidth
              onClick={cancelDetails}
              variant="outlined"
            >
              {t('RealTimeDetails.button.back')}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

export default RealTimeParticipants;
